import React, { useState } from 'react';
import Select from 'react-select';
import { X, Plus, Trash2, MessageSquare } from 'lucide-react';
import { useTheme } from '../../../components/common/Theme/ThemeContext';

interface IntentRule {
    phrase: string;
    requiredEntities: string[];
    priority: number;
}

const mockWorkflowOptions = [
    { value: 'Generate Invoice', label: 'Generate Invoice' },
    { value: 'Send Email', label: 'Send Email' },
    { value: 'Create Contact', label: 'Create Contact' },
    { value: 'Apply Leave', label: 'Apply Leave' }
];

const CreateIntentModelModal = ({ onClose }: { onClose: () => void }) => {
    const { theme } = useTheme();
    const isLight = theme === 'white';
    const inputCls = isLight
        ? 'bg-white border border-neutral-300 text-neutral-900 placeholder-neutral-400'
        : 'bg-neutral-800 text-white';
    const inputDarkCls = isLight
        ? 'bg-neutral-50 border border-neutral-300 text-neutral-900 placeholder-neutral-400'
        : 'bg-black text-white';
    const labelCls = isLight ? 'text-neutral-500' : 'text-gray-400';

    const selectStyles = {
        control: (base: any, state: any) => ({
            ...base,
            backgroundColor: isLight ? '#ffffff' : '#1c1c1c',
            borderColor: state.isFocused
                ? isLight ? '#818cf8' : '#6366f1'
                : isLight ? '#d4d4d8' : '#333',
            color: isLight ? '#171717' : 'white',
            padding: '2px',
            boxShadow: state.isFocused
                ? isLight ? '0 0 0 1px rgba(129,140,248,0.3)' : '0 0 0 1px rgba(99,102,241,0.3)'
                : 'none',
            cursor: 'pointer',
            '&:hover': { borderColor: isLight ? '#a1a1aa' : '#555' },
        }),
        menu: (base: any) => ({
            ...base,
            backgroundColor: isLight ? '#ffffff' : '#1c1c1c',
            color: isLight ? '#171717' : 'white',
            zIndex: 9999,
            border: isLight ? '1px solid #e5e5e5' : 'none',
            boxShadow: isLight ? '0 4px 12px rgba(0,0,0,0.1)' : base.boxShadow,
        }),
        option: (base: any, state: any) => ({
            ...base,
            backgroundColor: state.isFocused
                ? isLight ? '#f5f5f5' : '#2a2a2a'
                : isLight ? '#ffffff' : '#1c1c1c',
            color: isLight ? '#171717' : 'white',
            cursor: 'pointer',
        }),
        multiValue: (base: any) => ({
            ...base,
            backgroundColor: isLight ? '#e0e7ff' : '#333',
        }),
        multiValueLabel: (base: any) => ({
            ...base,
            color: isLight ? '#3730a3' : 'white',
        }),
        multiValueRemove: (base: any) => ({
            ...base,
            color: isLight ? '#6366f1' : '#ccc',
            ':hover': {
                backgroundColor: isLight ? '#c7d2fe' : '#555',
                color: isLight ? '#312e81' : 'white',
            },
        }),
        singleValue: (base: any) => ({
            ...base,
            color: isLight ? '#171717' : 'white',
        }),
        input: (base: any) => ({
            ...base,
            color: isLight ? '#171717' : 'white',
        }),
        placeholder: (base: any) => ({
            ...base,
            color: isLight ? '#a1a1aa' : '#6b7280',
        }),
        dropdownIndicator: (base: any) => ({
            ...base,
            color: isLight ? '#a1a1aa' : '#6b7280',
            '&:hover': { color: isLight ? '#525252' : '#d4d4d8' },
        }),
        indicatorSeparator: (base: any) => ({
            ...base,
            backgroundColor: isLight ? '#e5e5e5' : '#333',
        }),
    };

    const [intentName, setIntentName] = useState('');
    const [description, setDescription] = useState('');
    const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
    const [userOptions, setUserOptions] = useState<any[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

    const [linkedWorkflows, setLinkedWorkflows] = useState<string[]>([]);
    const [rules, setRules] = useState<IntentRule[]>([
        { phrase: '', requiredEntities: [''], priority: 1 }
    ]);
    const [sampleInput, setSampleInput] = useState('');
    const [testOutput, setTestOutput] = useState<string>('');

    const addRule = () => {
        setRules([...rules, { phrase: '', requiredEntities: [''], priority: 1 }]);
    };

    const updateRule = <K extends keyof IntentRule>(
        index: number,
        key: K,
        value: IntentRule[K]
    ) => {
        const updatedRules = [...rules];
        updatedRules[index][key] = value;
        setRules(updatedRules);
    };

    const removeRule = (index: number) => {
        const updatedRules = rules.filter((_, i) => i !== index);
        setRules(updatedRules);
    };

    const handleSubmit = () => {
        const payload = {
            name: intentName,
            description,
            workflows: linkedWorkflows,
            rules
        };
        console.log('Saving intent model:', payload);
        onClose();
    };

    const handleTest = () => {
        setTestOutput(`Detected intent: 'mock_intent'\nExtracted entities: { name: 'John', email: 'john@email.com' }`);
    };

    return (
        <div className={`fixed inset-0 z-[100] flex items-center justify-center backdrop-blur-md ${isLight ? 'bg-black/40' : 'bg-black bg-opacity-80'}`}>
            <div className={`w-[90vw] max-w-5xl p-6 rounded-xl shadow-xl overflow-y-auto max-h-[90vh] ${isLight ? 'bg-white text-neutral-900' : 'bg-neutral-900 text-white'}`}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className={`text-lg font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>Create New Intent Model</h2>
                    <button onClick={onClose} className={isLight ? 'text-neutral-400 hover:text-red-500' : 'hover:text-red-400'}>
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="space-y-4">
                    <div>
                        <label className={`block text-sm mb-1 ${labelCls}`}>Intent Name</label>
                        <input
                            type="text"
                            className={`w-full p-2 rounded text-sm ${inputCls}`}
                            value={intentName}
                            onChange={(e) => setIntentName(e.target.value)}
                            placeholder="e.g. Invoice Intents"
                        />
                    </div>

                    <div>
                        <label className={`block text-sm mb-1 ${labelCls}`}>Description</label>
                        <textarea
                            className={`w-full p-2 rounded text-sm resize-none ${inputCls}`}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Describe the purpose of this intent group..."
                        />
                    </div>

                    <div>
                        <label className={`block text-sm mb-1 ${labelCls}`}>Linked Workflows</label>
                        <Select
                            isMulti
                            isSearchable={false}
                            options={mockWorkflowOptions}
                            value={mockWorkflowOptions.filter(option => linkedWorkflows.includes(option.value))}
                            onChange={(selected) =>
                                setLinkedWorkflows(selected.map(option => option.value))
                            }
                            styles={selectStyles}
                        />
                    </div>

                    <div>
                        <label className={`block text-sm mb-1 ${labelCls}`}>Roles</label>
                        <Select
                            isMulti
                            isSearchable={false}
                            options={[
                                { value: 'admin', label: 'Admin' },
                                { value: 'manager', label: 'Manager' },
                                { value: 'auditor', label: 'Auditor' },
                                { value: 'finance', label: 'Finance Team' },
                                { value: 'developer', label: 'Developer' },
                            ]}
                            value={selectedRoles.map((r) => ({ value: r, label: r }))}
                            onChange={(selectedOptions) =>
                                setSelectedRoles(selectedOptions.map((opt) => opt.value))
                            }
                            placeholder="Select roles..."
                            styles={selectStyles}
                        />
                    </div>
                    <div>
                        <label className={`block text-sm mb-1 ${labelCls}`}>Users</label>
                        <Select
                            isMulti
                            isSearchable={false}
                            options={[
                                { value: 'John Doe', label: 'John Doe' },
                                { value: 'Maryam Shah', label: 'Maryam Shah' },
                                { value: 'Ali Raza', label: 'Ali Raza' },
                                { value: 'Sana Khan', label: 'Sana Khan' },
                                { value: 'Daniel Lee', label: 'Daniel Lee' },
                            ]}
                            value={selectedUsers.map((id) => {
                                const match = userOptions.find((u) => u.value === id);
                                return match || { value: id, label: id };
                            })}
                            onChange={(selectedOptions) =>
                                setSelectedUsers(selectedOptions.map((opt) => opt.value))
                            }
                            placeholder="Assign users..."
                            styles={selectStyles}
                        />
                    </div>

                    <div>
                        <label className={`block text-sm mb-2 ${labelCls}`}>Intent Rules</label>
                        {rules.map((rule, index) => (
                            <div key={index} className={`p-3 rounded mb-3 ${isLight ? 'bg-neutral-50 border border-neutral-200' : 'bg-neutral-800'}`}>
                                <div className="mb-2">
                                    <label className={`text-xs block mb-1 ${isLight ? 'text-neutral-600' : ''}`}>Trigger Phrase</label>
                                    <input
                                        type="text"
                                        value={rule.phrase}
                                        onChange={(e) => updateRule(index, 'phrase', e.target.value)}
                                        className={`w-full p-2 rounded text-xs ${inputDarkCls}`}
                                        placeholder="e.g. create an invoice"
                                    />
                                </div>
                                <div className="mb-2">
                                    <label className={`text-xs block mb-1 ${isLight ? 'text-neutral-600' : ''}`}>Required Entities (comma separated)</label>
                                    <input
                                        type="text"
                                        value={rule.requiredEntities.join(', ')}
                                        onChange={(e) => updateRule(index, 'requiredEntities', e.target.value.split(',').map(v => v.trim()))}
                                        className={`w-full p-2 rounded text-xs ${inputDarkCls}`}
                                        placeholder="client_name, amount, due_date"
                                    />
                                </div>
                                <div className="flex justify-between items-center">
                                    <div>
                                        <label className={`text-xs block mb-1 ${isLight ? 'text-neutral-600' : ''}`}>Priority</label>
                                        <input
                                            type="number"
                                            value={rule.priority}
                                            onChange={(e) => updateRule(index, 'priority', parseInt(e.target.value))}
                                            className={`w-24 p-2 rounded text-xs ${inputDarkCls}`}
                                        />
                                    </div>
                                    <button onClick={() => removeRule(index)} className="text-red-400 hover:text-red-300 mt-4">
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                        <button
                            onClick={addRule}
                            className={`text-xs px-3 py-1.5 rounded flex items-center gap-1 transition-colors ${isLight
                                ? 'bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200'
                                : 'bg-neutral-700 hover:bg-neutral-600 text-white'
                                }`}
                        >
                            <Plus className="w-4 h-4" /> Add Rule
                        </button>
                    </div>

                    <div>
                        <label className={`block text-sm mb-1 ${labelCls}`}>Test Input</label>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                className={`flex-1 p-2 rounded text-sm ${inputCls}`}
                                value={sampleInput}
                                onChange={(e) => setSampleInput(e.target.value)}
                                placeholder="e.g. Generate an invoice for John Doe"
                            />
                            <button
                                onClick={handleTest}
                                className={`${isLight ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-blue-600 hover:bg-blue-700'
                                    } text-white px-4 py-2 rounded text-sm flex items-center gap-2 transition-colors`}
                            >
                                <MessageSquare className="w-4 h-4" /> Test
                            </button>
                        </div>
                        {testOutput && (
                            <pre className={`mt-2 p-3 rounded text-xs whitespace-pre-wrap ${isLight ? 'bg-neutral-900 text-green-400' : 'bg-black text-green-400'}`}>
                                {testOutput}
                            </pre>
                        )}
                    </div>
                </div>

                <div className={`mt-6 flex justify-end gap-3 border-t pt-4 ${isLight ? 'border-neutral-200' : 'border-neutral-800'}`}>
                    <button
                        onClick={onClose}
                        className={`text-sm font-medium ${isLight ? 'text-neutral-500 hover:text-neutral-900 px-4 py-2 rounded-md hover:bg-neutral-100' : 'text-gray-400 hover:text-white'}`}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        className={`${isLight ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-green-700 hover:bg-green-800'
                            } text-white px-5 py-2 rounded-md text-sm font-medium shadow-sm transition-colors`}
                    >
                        Save Intent Model
                    </button>
                </div>

            </div>
        </div>
    );
};

export default CreateIntentModelModal;
